if (!customElements.get('monogram-option')) {
  class MonogramOption extends HTMLElement {
    connectedCallback() {
      this.maxLength = parseInt(this.dataset.maxLength, 10) || 12;
      this.feeVariantId = (this.dataset.feeVariantId || '').trim();
      this.productTitle = this.dataset.productTitle || '';

      this.toggle = this.querySelector('[data-monogram-toggle]');
      this.hiddenInput = this.querySelector('[data-monogram-input]');
      this.cta = this.querySelector('[data-monogram-cta]');
      this.summary = this.querySelector('[data-monogram-summary]');
      this.summaryText = this.querySelector('[data-monogram-display]');
      this.editButton = this.querySelector('[data-monogram-edit]');
      this.removeButton = this.querySelector('[data-monogram-remove]');
      this.dialog = this.querySelector('[data-monogram-dialog]');
      this.field = this.querySelector('[data-monogram-field]');
      this.counter = this.querySelector('[data-monogram-count]');
      this.error = this.querySelector('[data-monogram-error]');
      this.saveButton = this.querySelector('[data-monogram-save]');
      this.cancelButtons = this.querySelectorAll('[data-monogram-cancel]');

      this.productForm = this.closest('product-form');
      this.form = this.productForm ? this.productForm.querySelector('form') : null;

      this.cta.addEventListener('click', this.onCtaClick.bind(this));
      this.editButton.addEventListener('click', this.openDialog.bind(this));
      this.removeButton.addEventListener('click', this.onRemove.bind(this));
      this.saveButton.addEventListener('click', this.onSave.bind(this));
      this.cancelButtons.forEach((btn) => btn.addEventListener('click', this.onCancel.bind(this)));
      this.field.addEventListener('input', this.onFieldInput.bind(this));
      this.field.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.onSave();
        }
      });

      this.dialog.addEventListener('close', () => {
        if (!this.hiddenInput.value) {
          this.toggle.checked = false;
          this.showCta();
        }
        this.unlockBodyScroll();
      });

      // Intercept the product form submit BEFORE product-form.js sees it,
      // so we can add the fee item as a second line in the same /cart/add call.
      if (this.form && this.feeVariantId) {
        this.form.addEventListener('submit', this.onFormSubmit.bind(this), { capture: true });
      }
    }

    onCtaClick() {
      this.field.value = this.hiddenInput.value || '';
      this.updateCounter();
      this.openDialog();
    }

    onRemove() {
      this.clearMonogram();
      this.toggle.checked = false;
      this.showCta();
    }

    showCta() {
      this.cta.hidden = false;
      this.summary.hidden = true;
      // Drop the active flag so the dynamic checkout buttons (Shop Pay, Apple
      // Pay, etc.) re-appear. They bypass our form-submit interceptor and would
      // create an instant checkout without the monogram fee item.
      this.removeAttribute('data-active');
    }

    showSummary() {
      this.cta.hidden = true;
      this.summary.hidden = false;
      this.setAttribute('data-active', 'true');
    }

    openDialog() {
      this.error.hidden = true;

      // Lock body scroll. iOS Safari otherwise lets the page scroll behind
      // the dialog and positions the dialog against the layout viewport,
      // leaving the dialog top hidden above the visible area.
      this.lockBodyScroll();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      const inner = this.dialog.querySelector('.monogram-option__dialog-inner');
      const resetScroll = () => {
        if (inner) inner.scrollTop = 0;
        this.dialog.scrollTop = 0;
      };
      resetScroll();
      requestAnimationFrame(() => {
        resetScroll();
        try {
          this.field.focus({ preventScroll: true });
        } catch (_) {
          this.field.focus();
        }
        resetScroll();
      });
    }

    closeDialog() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      this.unlockBodyScroll();
    }

    lockBodyScroll() {
      if (this._bodyLocked) return;
      this._bodyLocked = true;
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      this._savedBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
      };
      body.style.position = 'fixed';
      body.style.top = `-${this._savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }

    unlockBodyScroll() {
      if (!this._bodyLocked) return;
      this._bodyLocked = false;
      const body = document.body;
      const styles = this._savedBodyStyles || {};
      body.style.position = styles.position || '';
      body.style.top = styles.top || '';
      body.style.left = styles.left || '';
      body.style.right = styles.right || '';
      body.style.width = styles.width || '';
      window.scrollTo(0, this._savedScrollY || 0);
    }

    onFieldInput() {
      if (this.field.value.length > this.maxLength) {
        this.field.value = this.field.value.slice(0, this.maxLength);
      }
      this.updateCounter();
      if (this.field.value.trim().length > 0) this.error.hidden = true;
    }

    updateCounter() {
      this.counter.textContent = this.field.value.length;
    }

    onSave() {
      const value = this.field.value.trim();
      if (!value) {
        this.error.hidden = false;
        this.field.focus();
        return;
      }
      this.hiddenInput.value = value;
      this.hiddenInput.disabled = false;
      this.summaryText.textContent = value;
      this.toggle.checked = true;
      this.showSummary();
      this.closeDialog();
      // Confirming the name is the add to cart — no second click on the page.
      this.addAfterSave();
    }

    addAfterSave() {
      if (!this.feeVariantId) return;
      const submitButton = this.form && this.form.querySelector('[type="submit"]');
      // Sold out, or an add already in flight.
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;
      this.submitWithFee().catch((err) => console.error('[monogram] add-to-cart failed', err));
    }

    onCancel() {
      if (!this.hiddenInput.value) {
        this.toggle.checked = false;
        this.showCta();
      }
      this.closeDialog();
    }

    clearMonogram() {
      this.hiddenInput.value = '';
      this.hiddenInput.disabled = true;
      this.summaryText.textContent = '';
      this.field.value = '';
      this.updateCounter();
      this.showCta();
    }

    isMonogramOptedIn() {
      return this.toggle.checked && this.hiddenInput.value && !this.hiddenInput.disabled;
    }

    onFormSubmit(event) {
      if (!this.isMonogramOptedIn()) return; // let product-form.js handle it normally
      if (!this.feeVariantId) return;

      // Take over: prevent the default submit and product-form.js handler.
      event.preventDefault();
      event.stopImmediatePropagation();

      this.submitWithFee().catch((err) => console.error('[monogram] add-to-cart failed', err));
    }

    async submitWithFee() {
      const submitButton = this.form.querySelector('[type="submit"]');
      const submitText = submitButton ? submitButton.querySelector('span') : null;
      const spinner = this.form.querySelector('.loading__spinner');
      const productForm = this.productForm;

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');

      const variantInput = this.form.querySelector('[name="id"]');
      const quantityInput = this.form.querySelector('[name="quantity"]');
      const monogramName = this.hiddenInput.value;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      // Collect any non-monogram properties[*] fields from the form
      // (e.g. gift card recipient). The Monogram Name lives only on the
      // fee item so the main product line stays clean.
      const mainProperties = {};
      this.form.querySelectorAll('[name^="properties["]').forEach((el) => {
        if (el.disabled) return;
        const match = el.name.match(/^properties\[(.+)\]$/);
        if (!match) return;
        const key = match[1];
        if (key === 'Monogram Name') return;
        if (!el.value) return;
        mainProperties[key] = el.value;
      });

      const mainItem = {
        id: parseInt(variantInput.value, 10),
        quantity: parseInt(quantityInput ? quantityInput.value : 1, 10) || 1,
      };
      if (Object.keys(mainProperties).length > 0) {
        mainItem.properties = mainProperties;
      }

      const items = [
        mainItem,
        {
          id: parseInt(this.feeVariantId, 10),
          quantity: 1,
          properties: {
            'Monogram Name': monogramName,
            'Monogram for': this.productTitle,
          },
        },
      ];

      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
      }

      // Clear any stale error from a previous attempt.
      this.handleError(false);

      let response;
      let data;
      try {
        response = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        data = await response.json();
      } catch (err) {
        console.error('[monogram] cart/add network/parse error', err);
        this.handleError('Could not add to cart. Please try again.');
        this.resetSubmitButton(submitButton, spinner);
        return;
      }

      if (!response.ok) {
        console.error('[monogram] cart/add returned', response.status, data);
        this.handleError(data?.description || data?.message || 'Could not add to cart.');
        this.resetSubmitButton(submitButton, spinner);
        return;
      }

      // Multi-item response is { items: [...], sections: {...} }, but
      // cart-notification.renderContents expects a single-item shape with
      // `key`/`id` at the top. Hoist them from the main product line so the
      // cart-notification can find the right product to show.
      if (data && Array.isArray(data.items) && data.items[0]) {
        if (!data.key) data.key = data.items[0].key;
        if (!data.id) data.id = data.items[0].id;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'monogram-option',
          productVariantId: variantInput.value,
          cartData: data,
        });
      }

      // Render in its own try/catch — the cart add already succeeded, so a
      // render failure must not surface as "Could not add to cart".
      try {
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (renderErr) {
        console.error('[monogram] cart render failed (items WERE added)', renderErr);
      }

      // Intentionally NOT calling clearMonogram() here. Resetting the tile
      // back to the empty CTA after a successful add lets the express
      // checkout buttons (Shop Pay, Apple Pay, etc.) reappear, and a user
      // who then clicks one would land on a checkout that ignores the cart
      // entirely — losing the monogram and creating a duplicate order.
      // Persist the summary + data-active flag like variant/quantity
      // selections persist; the user removes it explicitly via the Remove
      // button when they want a fresh state.
      this.resetSubmitButton(submitButton, spinner);
      if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    }

    resetSubmitButton(submitButton, spinner) {
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.add('hidden');
    }

    handleError(message) {
      if (!this.productForm) return;
      const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      const text = this.productForm.querySelector('.product-form__error-message');
      if (!wrapper || !text) return;
      if (!message) {
        wrapper.toggleAttribute('hidden', true);
        text.textContent = '';
        return;
      }
      wrapper.toggleAttribute('hidden', false);
      text.textContent = message;
    }
  }

  customElements.define('monogram-option', MonogramOption);
}
